import { concatRawBuffers, createTextEncoder } from '#common/util';
import { InvalidMessageError } from './types/errors';
var MessageType;
(function (MessageType) {
    MessageType[MessageType["REQUEST"] = 10] = "REQUEST";
    MessageType[MessageType["RESPONSE"] = 11] = "RESPONSE";
})(MessageType || (MessageType = {}));
var OptionalHeaderType;
(function (OptionalHeaderType) {
    OptionalHeaderType[OptionalHeaderType["SRC_PATH"] = 1] = "SRC_PATH";
    OptionalHeaderType[OptionalHeaderType["DEST_PATH"] = 2] = "DEST_PATH";
})(OptionalHeaderType || (OptionalHeaderType = {}));
const WSS_SRC_PATH = 'WebClient';
const MESSAGE_HEADER_OFFSET = 8; // MessageType + TotalHeaderLength + TotalBodyLength
const PATH_HEADER_OFFSET = 4; // MessageType + Reserved bit + OptHeaderLength
export class WrappedProtobufMessage {
    static wrap(protobufMessage, destPath) {
        const destPathHeader = WrappedProtobufMessage.makeOptHeader(OptionalHeaderType.DEST_PATH, destPath);
        const srcPathHeader = WrappedProtobufMessage.makeOptHeader(OptionalHeaderType.SRC_PATH, WSS_SRC_PATH);
        const totalBodyLength = protobufMessage.length;
        const totalHeaderLength = MESSAGE_HEADER_OFFSET + destPathHeader.length + srcPathHeader.length;
        return concatRawBuffers([
            WrappedProtobufMessage.toBytesInt16(MessageType.REQUEST),
            WrappedProtobufMessage.toBytesInt16(totalHeaderLength),
            WrappedProtobufMessage.toBytesInt32(totalBodyLength),
            destPathHeader,
            srcPathHeader,
            protobufMessage,
        ]);
    }
    static unwrap(wrappedProtobufMessage) {
        const responseType = WrappedProtobufMessage.parseInt16(wrappedProtobufMessage, 0);
        const totalHeaderLength = WrappedProtobufMessage.parseInt16(wrappedProtobufMessage, 2);
        const totalBodyLength = WrappedProtobufMessage.parseInt32(wrappedProtobufMessage, 4);
        if (responseType !== MessageType.RESPONSE) {
            throw new InvalidMessageError("Response type doesn't match");
        }
        if (totalBodyLength + totalHeaderLength !== wrappedProtobufMessage.length) {
            throw new InvalidMessageError("Message length doesn't match");
        }
        return wrappedProtobufMessage.slice(totalHeaderLength, wrappedProtobufMessage.length);
    }
    static toBytesInt8(num) {
        const arr = new ArrayBuffer(1);
        const view = new DataView(arr);
        const offset = 0;
        view.setUint8(offset, num);
        return new Uint8Array(arr);
    }
    static toBytesInt16(num) {
        const arr = new ArrayBuffer(2);
        const view = new DataView(arr);
        const offset = 0;
        const isLittleEndian = false;
        view.setUint16(offset, num, isLittleEndian);
        return new Uint8Array(arr);
    }
    static toBytesInt32(num) {
        const arr = new ArrayBuffer(4);
        const view = new DataView(arr);
        const offset = 0;
        const isLittleEndian = false;
        view.setUint32(offset, num, isLittleEndian);
        return new Uint8Array(arr);
    }
    static makeOptHeader(type, content) {
        const enc = createTextEncoder();
        const reserved = 0;
        const optHeaderLength = PATH_HEADER_OFFSET + content.length;
        return concatRawBuffers([
            WrappedProtobufMessage.toBytesInt8(type),
            WrappedProtobufMessage.toBytesInt8(reserved),
            WrappedProtobufMessage.toBytesInt16(optHeaderLength),
            enc.encode(content),
        ]);
    }
    static parseInt16(array, offset) {
        return this.parseInt(array, offset, 2);
    }
    static parseInt32(array, offset) {
        return this.parseInt(array, offset, 4);
    }
    static parseInt(array, offset, intSize) {
        if (array.length < offset + intSize - 1) {
            throw new InvalidMessageError('Cannot parse message header');
        }
        let res = 0;
        for (let i = 0; i < intSize; ++i) {
            res = res * 256 + array.at(offset + i);
        }
        return res;
    }
}
